import {Component, DestroyRef, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {SharedModule} from '../../shared/shared.module';
import {
  ModificationButtonsComponent
} from 'src/app/shared/components/modification-buttons/modification-buttons.component';
import {ActivatedRoute} from "@angular/router";
import {FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {Alert, AlertService, AlertType} from "@michelin/theme";
import {TranslateService} from "@ngx-translate/core";
import {ContextService} from "../../shared/services/context.service";
import {
  BehaviorSubject, combineLatest,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  startWith,
  Subscription,
  tap
} from "rxjs";
import {TireService} from "../../shared/services/tire.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AutocompleteComponent} from "../../shared/components/autocomplete/autocomplete.component";
import {TruckTires} from "../../shared/models/tire/TruckTires";
import {Tires} from "../../shared/models/tire/Tires";
import {TireSpec} from "../../shared/models/tire/TireSpec";
import {TruckTiresTableComponent} from 'src/app/pages/tire/truck-tires-table/truck-tires-table.component';
import {TruckSpec} from "../../shared/models/tire/TruckSpec";
import {IModif} from "../../shared/models/IModif";
import {LINE_TYPE_TIRE_DETAIL, LINE_TYPE_TRUCK} from "src/app/shared/constants/TireConstants";
import {TireConfRequest} from "../../shared/models/tire/request/TireConfRequest";
import {TireRequest} from "../../shared/models/tire/request/TireRequest";
import {TireLineForm} from "../../shared/models/tire/TireLineForm";
import {TruckTireRequest} from "../../shared/models/tire/request/TruckTireRequest";
import {TruckTireDeletedRequest} from "../../shared/models/tire/request/TruckTireDeletedRequest";

@Component({
  selector: 'tkc-tire',
  standalone: true,
  imports: [SharedModule, ModificationButtonsComponent, AutocompleteComponent, TruckTiresTableComponent],
  templateUrl: './tire.component.html',
  styleUrls: ['./tire.component.scss']
})
export class TireComponent implements OnInit, IModif, OnDestroy {

  @ViewChild(TruckTiresTableComponent) truckTiresTable: TruckTiresTableComponent;
  private siteId: number | null = null;
  private destroyRef = inject(DestroyRef);
  isLoading: boolean = false;
  isModificationPending: boolean = false;

  // Autocomplete filter Truck Spec
  truckSpecfilterCtrl: FormControl = new FormControl<string>('');
  truckSpecfilteredList$: Observable<TruckSpec[]>;
  truckSpecList: TruckSpec[] = new Array<TruckSpec>();
  isFilterTruckLoading: boolean = false;

  // Autocomplete filter Tire Spec
  tireSpecfilterCtrl: FormControl = new FormControl<string>('');
  tireSpecfilteredList$: Observable<TireSpec[]>;
  tireSpecList: TireSpec[] = new Array<TireSpec>();
  isFilterTireLoading: boolean = false;

  truckTiresList: Array<TruckTires>;
  saveButtonTooltipContent: string | undefined = undefined;
  isErrorTooltipSaveBtn: boolean = false;

  protected selectedTruckSpecId$ = new BehaviorSubject<number | undefined>(undefined);
  protected selectedTireSpecId$ = new BehaviorSubject<number | undefined>(undefined);

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private translate: TranslateService,
    private tireService: TireService,
    private contextService: ContextService
  ) {
    this.contextService.register(this)
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.saveButtonTooltipContent = this.translate.instant('tire.tooltip.total-incorrect');
      });
  }

  getModifStatus(): boolean {
    return this.isModificationPending
  }

  /**
   * Initializes the component by subscribing to the siteId
   * from the context service and loading the tire information for that site.
   * It also resets the truck and tire spec filters when the site changes.
   */
  ngOnInit(): void {
    this.contextService.siteId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      if (id !== null && id !== this.siteId) {
        this.siteId = id;
        this.isModificationPending = false;
        this.resetTruckSpecFilter();
        this.resetTireSpecFilter();
        this.loadTireInfos();
      }
    });
  }

  loadTireInfos(): void {
    this.isLoading = true
    this.tireService.loadTireInfosBySite(this.siteId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tires:Tires) => {
          this.truckSpecList = tires.truckSpecs;
          this.tireSpecList = tires.tireSpecs;
          this.truckTiresList = tires.truckTires;
          this.initAutocompleteTruckSpec();
          this.initAutocompleteTireSpec();
        },
        error: (err) => {
          console.log("error")
        },
        complete: () => {
          this.isLoading = false
      }
    })
  }

  /**
   * Initializes the autocomplete functionality for the truck specifications.
   * @private
   */
  private initAutocompleteTruckSpec() {
    const truckSearch$ = this.truckSpecfilterCtrl.valueChanges.pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : (value?.specification ?? ''))
    );

    this.truckSpecfilteredList$ = combineLatest([truckSearch$, this.selectedTireSpecId$]).pipe(
      distinctUntilChanged(([prevSearch, prevTruckId], [currSearch, currTruckId]) =>
        prevSearch === currSearch && prevTruckId === currTruckId
      ),
      tap(() => {
        this.isFilterTruckLoading = true;
      }),
      map(([search, selectedTireSpecId]) => {
        const truckSpecs = this.filterTruckSpecs(search || '');
        //Filter by selected Tire Spec selected
        if (selectedTireSpecId) {
          const truckSpecsFiltered = this.truckTiresList.filter(truckTire => truckTire.tiresConf.some(conf => conf.tireSpecId === this.selectedTireSpecId$.value))
            .map(truckTire => truckTire.truckSpecId)
            .filter(truckSpecId => truckSpecs.some(truckSpec => truckSpec.truckSpecPk === truckSpecId));
          return truckSpecs.filter(truckSpec => truckSpecsFiltered.includes(truckSpec.truckSpecPk));
        }
        return truckSpecs;
      }),
      tap(() => {
        this.isFilterTruckLoading = false;
      }),
      finalize(() => {
        this.isFilterTruckLoading = false;
      })
    );
  }

  /**
   * Initializes the autocomplete functionality for the tire specifications.
   * @private
   */
  private initAutocompleteTireSpec() {
    const tireSearch$ = this.tireSpecfilterCtrl.valueChanges.pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : (value?.specificationFull ?? ''))
    );

    this.tireSpecfilteredList$ = combineLatest([tireSearch$, this.selectedTruckSpecId$]).pipe(
      distinctUntilChanged(([prevSearch, prevTireId], [currSearch, currTireId]) =>
        prevSearch === currSearch && prevTireId === currTireId
      ),
      tap(() => {
        this.isFilterTireLoading = true;
      }),
      map(([search, selectedTruckSpecId]) => {
        let tireSpecs = this.filterTireSpecs(search || '');
        if (selectedTruckSpecId) {
          const tireSpecsFiltered = this.truckTiresList.filter(truckTire => truckTire.tiresConf.some(tireConf => tireConf.truckSpecId === this.selectedTruckSpecId$.value))
            .map(truckTire => truckTire.tiresConf.map(conf => conf.tireSpecId))
            .flat()
            .filter(tireSpecId => tireSpecs.some(tireSpec => tireSpec.id === tireSpecId));
          return tireSpecs.filter(tireSpec => tireSpecsFiltered.includes(tireSpec.id));
        }
        return tireSpecs;
      }),
      tap(() => {
        this.isFilterTireLoading = false;
      }),
      finalize(() => {
        this.isFilterTireLoading = false;
      })
    );
  }

  onTruckSelected(truckSpec: TruckSpec): void {
    this.truckSpecfilterCtrl.setValue(truckSpec?.specification ?? '', { emitEvent: true });
    this.selectedTruckSpecId$.next(truckSpec.truckSpecPk);
    this.initAutocompleteTireSpec();
  }

  protected resetTruckSpecFilter() {
    this.truckSpecfilterCtrl.setValue('');
    this.selectedTruckSpecId$.next(undefined);
  }

  onTireSelected(tireSpec: TireSpec): void {
    this.tireSpecfilterCtrl.setValue(tireSpec?.specificationFull ?? '', { emitEvent: true });
    this.selectedTireSpecId$.next(tireSpec.id);
  }

  protected resetTireSpecFilter() {
    this.tireSpecfilterCtrl.setValue('');
    this.selectedTireSpecId$.next(undefined);
  }

  private filterTruckSpecs(search: string): TruckSpec[] {
    const filterValue = search.toLowerCase();
    return this.truckSpecList.filter((truckSpec) => truckSpec.specification.toLowerCase().includes(filterValue));
  }

  private filterTireSpecs(search: string): TireSpec[] {
    const filterValue = search.toLowerCase();
    if (filterValue.trim().length > 0) {
      return this.tireSpecList.filter((tireSpec) => tireSpec.specificationFull.toLowerCase().includes(filterValue));
    }
    return this.tireSpecList;
  }

  /**
   * Saves the modifications
   */
  saveModifications(): void {
    const tireSettingsForm:FormArray<FormGroup<TireLineForm>> = this.truckTiresTable.tireSettings;
    if (!this.isValidForm(tireSettingsForm))
      return;

    this.isLoading = true;
    const truckTiresDeleted:TruckTireDeletedRequest[] = this.truckTiresTable.deletedTruckTires
    const truckTiresRequest:TruckTireRequest[] = this.getTruckTiresRequestFromForm(tireSettingsForm);

    const tireRequest:TireRequest = {
      siteId: this.siteId,
      truckTires: truckTiresRequest,
      truckTireDeleted : truckTiresDeleted
    }


    //Save expanded states of the table to restore them after the save operation
    this.truckTiresTable.saveExpandedStates()

    this.tireService.saveTireSettings(tireRequest).subscribe({
      next: (response) => {
        this.truckTiresTable.deletedTruckTires = [];
        tireSettingsForm.controls.filter(g => g.controls.type.value === LINE_TYPE_TIRE_DETAIL).forEach(g => {
          g.controls.updated.setValue(false);
          g.controls.added.setValue(false);
        })
        this.isModificationPending = false;
        this.isLoading = false;
        this.alertService.showAlert({
          type: AlertType.success,
          title: this.translate.instant('tire.saveSuccessAlert.title'),
          content: this.translate.instant('tire.saveSuccessAlert.content')
        } as Alert);
      },
      error: (err) => {
        let errorMsg: string | undefined = undefined;
        if (err.error && err.error.message) {
          errorMsg = `(${err.error.message})`;
        }
        this.alertService.showAlert({
          type: AlertType.critical,
          title: this.translate.instant('tire.error.title'),
          content: `${this.translate.instant('tire.error.msg')} ${errorMsg}`
        } as Alert);
        this.isLoading = false;
      },
    })
  }

  /**
   * Validates the form to ensure that any comment entered is associated with a manual hours update.
   * @param tireSettingsForm
   */
  isValidForm(tireSettingsForm:FormArray<FormGroup<TireLineForm>>): boolean {
    let errors:string[] = [];
    tireSettingsForm.controls
      .filter(g => g.controls.type.value === LINE_TYPE_TRUCK)
      .forEach(group => {
        let truckSpecId = group.controls.truckSpecId.value;
        tireSettingsForm.controls
          .filter(g => g.controls.type.value === LINE_TYPE_TIRE_DETAIL && g.controls.truckSpecId.value === truckSpecId)
          .forEach(group => {
              let manualHours:number|undefined = group.controls.manualHours?.value
              let comment:string|undefined = group.controls.comment?.value
              if (comment && comment.trim().length > 0 && (!manualHours || manualHours < 0)) {
                errors.push(this.translate.instant('tire.error.manual-hours-missing'));
              }
          });
      });
    if (errors.length > 0) {
        this.alertService.showAlert({
          type: AlertType.critical,
          title: this.translate.instant('tire.error.title'),
          content: errors.map(e => `- ${e}`).join('\n')
        } as Alert);
        return false;
    }
    return true;
  }

  /**
   * Transforms the form data into a list of TruckTireRequest objects to be sent to the backend.
   * @param tireSettingsForm
   * @private
   */
  private getTruckTiresRequestFromForm(tireSettingsForm:FormArray<FormGroup<TireLineForm>>): TruckTireRequest[] {
    let truckTiresRequest:TruckTireRequest[] = [];
    tireSettingsForm.controls
      .filter(g => g.controls.type.value === LINE_TYPE_TRUCK)
      .forEach(group => {
        let truckSpecId = group.controls.truckSpecId.value;
        let tireConfList:TireConfRequest[] = [];
        tireSettingsForm.controls
          .filter(g => g.controls.type.value === LINE_TYPE_TIRE_DETAIL && g.controls.truckSpecId.value === truckSpecId)
          .forEach(group => {
            let tireConfRequest: TireConfRequest = {
              tireLifeId: group.controls.tireLifeId?.value,
              truckSpecId: group.controls.truckSpecId?.value,
              tireSpecId: group.controls.tireSpecId?.value,
              tireSpecFull: group.controls.tireSpecificationFull?.value,
              targetedMix: group.controls.targetedMix?.value,
              originalHours: group.controls.originalHours?.value,
              manualHours: group.controls.manualHours?.value,
              comment: group.controls.comment?.value,
              updated: group.controls.updated?.value,
              added: group.controls.added?.value
            }
            tireConfList.push(tireConfRequest);
          });
        truckTiresRequest.push({
          truckSpecId: truckSpecId,
          tiresConf: tireConfList
        })
      });
    return truckTiresRequest;
  }

  /**
   * Resets the modifications made to the size settings.
   */
  resetModifications(): void {
    this.loadTireInfos();
    this.isModificationPending = false;
  }

  ngOnDestroy(): void {
    this.contextService.unRegister(this);
  }

}
